import { Fragment, memo, type ReactNode, useLayoutEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { globalMessages } from '@didaskill/ui/intl';
import { Truncate, useTruncate } from '@didaskill/ui/utils';

import { LinkButton } from '../link-button/LinkButton';
import { Typography } from '../typography/Typography';

type ShowMoreButtonProps = {
  isExpanded: boolean;
  onToggleExpand: () => void;
};

export type ExpandableTruncateProps = {
  /** Max number of lines to show before showing an ellipsis. */
  maxLines: number;
  /** Whether the content is expanded on first render. */
  initialExpanded?: boolean;
  /** Function to customize the "Show more" button. ({ isExpanded, onToggleExpand }) => Node. */
  showMoreButton?: (props: ShowMoreButtonProps) => ReactNode;
  /** Content to truncate. */
  children?: ReactNode;
};

const defaultShowMoreButton = ({ isExpanded, onToggleExpand }: ShowMoreButtonProps) => (
  <LinkButton onClick={onToggleExpand}>
    <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}>
      {isExpanded ? (
        <FormattedMessage {...globalMessages.COLLAPSE} />
      ) : (
        <FormattedMessage {...globalMessages.KNOW_MORE} />
      )}
    </Typography>
  </LinkButton>
);

/**
 * Content clamped to a max number of lines, with a "Show more" / "Collapse" button displayed only
 * when the content overflows.
 *
 * @param props.maxLines - Max number of lines displayed while collapsed.
 * @param props.initialExpanded - Expands the content on first render.
 * @param props.showMoreButton - Render function of the toggle button, replacing the default link.
 * @param props.children - Content to truncate.
 * @returns The truncated content and its toggle button.
 */
export const ExpandableTruncateBase = ({
  maxLines,
  initialExpanded = false,
  showMoreButton = defaultShowMoreButton,
  ...props
}: ExpandableTruncateProps): JSX.Element => {
  const {
    ref: truncateRef,
    isExpanded,
    contentOverflows,
    onExpand,
    onToggleExpand,
  } = useTruncate({ maxLines });

  /**
   * Always mount collapsed so the overflow is measured against the clamped height, then expand
   * before paint: mounting expanded would measure no overflow and hide the "Collapse" button.
   */
  useLayoutEffect(() => {
    if (initialExpanded) {
      onExpand();
    }
  }, [initialExpanded, onExpand]);

  return (
    <Fragment>
      <Truncate {...props} ref={truncateRef} maxLines={maxLines} isExpanded={isExpanded} />

      {!!contentOverflows && showMoreButton({ isExpanded, onToggleExpand })}
    </Fragment>
  );
};

ExpandableTruncateBase.displayName = 'ExpandableTruncate';

export const ExpandableTruncate = memo(ExpandableTruncateBase);
